import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { vendorAPI } from '../api/vendorAPI'
import { usePermissions } from '../contexts/PermissionContext'

function VendorUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [vendor, setVendor] = useState({
        name: '',
        full_name: '',
        contact: '',
        bill_to_address: '',
        ship_to_address: '',
        phone: '',
        email: '',
        is_active: true,
        shipping_lead_time_days: ''
    })

    useEffect(() => {
        async function fetchVendor() {
            try {
                const data = await vendorAPI.getById(id);
                setVendor(data)
            } catch (error) {
                console.error('Error fetching vendor:', error)
                setError('Failed to load vendor data')
            }
        }
        fetchVendor()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await vendorAPI.update(id, vendor);
            navigate('/vendors')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();
        
        if (vendor.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this vendor?')) {
                return;
            }
        }

        try {
            const updatedVendor = {
                ...vendor,
                is_active: !vendor.is_active
            };
            
            await vendorAPI.update(id, updatedVendor);
            setVendor(updatedVendor);
        } catch (error) {
            console.error('Error toggling vendor status:', error);
            alert(`Failed to update vendor: ${error.response?.data?.message || error.message}`);
        }
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('vendors.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to update vendors.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Update Vendor</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="name">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter name"
                                value={vendor.name}
                                onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="full_name">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter full name"
                                value={vendor.full_name}
                                onChange={(e) => setVendor({ ...vendor, full_name: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="contact">
                            <Form.Label>Contact</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter contact"
                                value={vendor.contact}
                                onChange={(e) => setVendor({ ...vendor, contact: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="bill_to_address">
                            <Form.Label>Bill To Address</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter billing address"
                                value={vendor.bill_to_address}
                                onChange={(e) => setVendor({ ...vendor, bill_to_address: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="ship_to_address">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Ship To Address</Form.Label>
                                {vendor.bill_to_address && vendor.is_active && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setVendor({...vendor, ship_to_address: vendor.bill_to_address})}
                                        style={{ fontSize: '0.875rem', padding: '0' }}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>
                                        Copy from Bill To
                                    </Button>
                                )}
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Enter shipping address"
                                value={vendor.ship_to_address}
                                onChange={(e) => setVendor({ ...vendor, ship_to_address: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="phone">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter phone"
                                value={vendor.phone}
                                onChange={(e) => setVendor({ ...vendor, phone: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email"
                                value={vendor.email}
                                onChange={(e) => setVendor({ ...vendor, email: e.target.value })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="shipping_lead_time_days">
                            <Form.Label>Shipping Lead Time (days)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                placeholder="Enter estimated shipping days"
                                value={vendor.shipping_lead_time_days ?? ''}
                                onChange={(e) => setVendor({ ...vendor, shipping_lead_time_days: e.target.value ? parseInt(e.target.value) : null })}
                                disabled={!vendor.is_active}
                                style={{ opacity: vendor.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                            />
                            <Form.Text className="text-muted">
                                Approximate number of days from order placement to delivery
                            </Form.Text>
                        </Form.Group>
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                {vendor.is_active ? (
                                    <>
                                        <Button 
                                            variant="primary" 
                                            type="submit" 
                                            className="me-2"
                                        >
                                            Update
                                        </Button>
                                        <Button 
                                            variant="danger"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Deactivate
                                        </Button>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => navigate('/vendors')}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </>
                                ) : (
                                    <Button 
                                        variant="success"
                                        onClick={handleToggleActive}
                                    >
                                        Activate
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {!vendor.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This vendor is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default VendorUpdateScreen 